import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Calendar, Copy, CheckCircle, RotateCw, Globe, HelpCircle } from 'lucide-react';
import { useLanguage } from '../i18n';

/**
 * 时间戳转换器组件
 * @param {Object} theme - 主题配置（橙色系）
 */
function TimestampConverter({ theme }) {
  const { t, language } = useLanguage();

  // 常用时区列表（根据语言动态生成）
  const TIMEZONES = language === 'zh' ? [
    { label: '本地时间', value: 'local' },
    { label: 'UTC (协调世界时)', value: 'UTC' },
    { label: '北京时间 (UTC+8)', value: 'Asia/Shanghai' },
    { label: '东京时间 (UTC+9)', value: 'Asia/Tokyo' },
    { label: '纽约时间 (UTC-5/-4)', value: 'America/New_York' },
    { label: '洛杉矶时间 (UTC-8/-7)', value: 'America/Los_Angeles' },
    { label: '伦敦时间 (UTC+0/+1)', value: 'Europe/London' },
    { label: '巴黎时间 (UTC+1/+2)', value: 'Europe/Paris' },
    { label: '悉尼时间 (UTC+10/+11)', value: 'Australia/Sydney' },
  ] : [
    { label: 'Local Time', value: 'local' },
    { label: 'UTC (Coordinated Universal Time)', value: 'UTC' },
    { label: 'Beijing (UTC+8)', value: 'Asia/Shanghai' },
    { label: 'Tokyo (UTC+9)', value: 'Asia/Tokyo' },
    { label: 'New York (UTC-5/-4)', value: 'America/New_York' },
    { label: 'Los Angeles (UTC-8/-7)', value: 'America/Los_Angeles' },
    { label: 'London (UTC+0/+1)', value: 'Europe/London' },
    { label: 'Paris (UTC+1/+2)', value: 'Europe/Paris' },
    { label: 'Sydney (UTC+10/+11)', value: 'Australia/Sydney' },
  ];

  const [timestamp, setTimestamp] = useState('');
  const [dateTime, setDateTime] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [copySuccess, setCopySuccess] = useState('');
  const [timezone, setTimezone] = useState('local'); // 选中的时区

  // 更新当前时间（每秒）
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // 根据时区格式化日期时间
  const formatDateTimeWithTimezone = (date, tz = 'local') => {
    if (tz === 'local') {
      return formatDateTime(date);
    }

    try {
      const options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: tz === 'UTC' ? 'UTC' : tz
      };

      const formatter = new Intl.DateTimeFormat('zh-CN', options);
      const parts = formatter.formatToParts(date);

      const year = parts.find(p => p.type === 'year').value;
      const month = parts.find(p => p.type === 'month').value;
      const day = parts.find(p => p.type === 'day').value;
      const hour = parts.find(p => p.type === 'hour').value;
      const minute = parts.find(p => p.type === 'minute').value;
      const second = parts.find(p => p.type === 'second').value;

      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    } catch (err) {
      return formatDateTime(date);
    }
  };

  // 时间戳 → 日期时间
  const convertTimestampToDate = (ts) => {
    if (!ts) return '';

    let milliseconds = parseInt(ts, 10);

    // 判断是秒还是毫秒（10位是秒，13位是毫秒）
    if (ts.length === 10) {
      milliseconds = milliseconds * 1000;
    }

    if (isNaN(milliseconds)) return 'Invalid timestamp';

    try {
      const date = new Date(milliseconds);
      if (date.toString() === 'Invalid Date') return 'Invalid timestamp';
      return formatDateTimeWithTimezone(date, timezone);
    } catch (err) {
      return 'Invalid timestamp';
    }
  };

  // 日期时间 → 时间戳
  const convertDateToTimestamp = (dateStr) => {
    if (!dateStr) return '';

    try {
      const date = new Date(dateStr);
      if (date.toString() === 'Invalid Date') return 'Invalid date';
      return Math.floor(date.getTime() / 1000);
    } catch (err) {
      return 'Invalid date';
    }
  };

  // 格式化日期时间
  const formatDateTime = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // 自动提取数字
  const extractNumbers = (str) => {
    // 提取连续的数字（10位或13位）
    const matches = str.match(/\d{10,13}/g);
    return matches ? matches[0] : str;
  };

  // 处理时间戳输入（带自动提取数字）
  const handleTimestampChange = (e) => {
    let value = e.target.value;

    // 如果包含非数字字符，尝试提取数字
    if (value && /\D/.test(value)) {
      const extracted = extractNumbers(value);
      if (extracted !== value && /^\d+$/.test(extracted)) {
        // 提取成功，显示提示并使用提取的值
        value = extracted;
      }
    }

    setTimestamp(value);

    if (value) {
      const converted = convertTimestampToDate(value);
      setDateTime(converted);
    } else {
      setDateTime('');
    }
  };

  // 当时区改变时，重新转换
  useEffect(() => {
    if (timestamp) {
      const converted = convertTimestampToDate(timestamp);
      setDateTime(converted);
    }
  }, [timezone]); // eslint-disable-line react-hooks/exhaustive-deps

  // 处理日期时间输入
  const handleDateTimeChange = (e) => {
    const value = e.target.value;
    setDateTime(value);

    if (value) {
      const converted = convertDateToTimestamp(value);
      setTimestamp(converted.toString());
    } else {
      setTimestamp('');
    }
  };

  // 使用当前时间
  const useCurrentTime = () => {
    const now = new Date();
    const ts = Math.floor(now.getTime() / 1000);
    setTimestamp(ts.toString());
    setDateTime(formatDateTime(now));
  };

  // 复制到剪贴板
  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(type);
      setTimeout(() => setCopySuccess(''), 2000);
    });
  };

  // 格式化当前时间的各种显示
  const currentTimestamp = Math.floor(currentTime.getTime() / 1000);
  const currentTimestampMs = currentTime.getTime();
  const currentISO = currentTime.toISOString();
  const currentUTC = currentTime.toUTCString();
  const currentLocal = formatDateTime(currentTime);

  return (
    <div className="bg-[#faf9f6] dark:bg-gray-900 py-8">
      <div className="px-8 space-y-6">
        {/* 标题 */}
        <div className="text-center mb-8 relative">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Clock size={36} className="text-orange-600 dark:text-orange-400" />
            <h1 className="text-4xl font-bold text-gray-800 dark:text-white">
              {t('timestamp.title')}
            </h1>
            {/* 帮助入口 */}
            <Link
              to="/help/timestamp"
              className="absolute right-0 top-0 flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-lg transition-colors"
              title={language === 'zh' ? '使用指南' : 'Guide'}
            >
              <HelpCircle size={16} />
              <span className="hidden sm:inline">{language === 'zh' ? '使用指南' : 'Guide'}</span>
            </Link>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            {language === 'zh' ? 'Unix 时间戳与人类可读时间的双向转换' : 'Bidirectional conversion between Unix timestamp and human readable time'}
          </p>
        </div>

        {/* 当前时间显示 */}
        <div className="bg-gradient-to-r from-orange-100 to-amber-100 dark:from-gray-800 dark:to-gray-700 rounded-xl shadow-md border border-orange-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2 text-orange-900 dark:text-gray-100">
              <Calendar size={24} className="text-orange-600 dark:text-orange-400" />
              {t('timestamp.currentTime')}
            </h2>
            <button
              onClick={useCurrentTime}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors flex items-center gap-2 text-sm font-medium shadow-sm"
            >
              <RotateCw size={16} />
              {language === 'zh' ? '使用当前时间' : 'Use Current Time'}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 可点击复制的当前时间项 */}
            <button
              onClick={() => copyToClipboard(currentLocal, 'current-local')}
              className="space-y-2 text-left p-3 rounded-lg hover:bg-white/60 dark:hover:bg-gray-700 transition-all group relative border border-orange-200 dark:border-gray-700 bg-white/40"
            >
              <p className="text-sm text-orange-700 dark:text-gray-400 flex items-center justify-between">
                {t('timestamp.output.localTime')}
                <Copy size={14} className="opacity-0 group-hover:opacity-100 transition-opacity text-orange-600 dark:text-orange-400" />
              </p>
              <p className="text-2xl font-mono font-bold text-orange-900 dark:text-gray-100">{currentLocal}</p>
              {copySuccess === 'current-local' && (
                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 text-green-600 dark:text-green-400 px-3 py-1 rounded-lg text-sm font-medium shadow-lg flex items-center gap-1 border border-gray-200 dark:border-gray-700">
                  <CheckCircle size={16} /> {t('common.copied')}
                </span>
              )}
            </button>

            <button
              onClick={() => copyToClipboard(currentTimestamp.toString(), 'current-ts')}
              onDoubleClick={() => {
                setTimestamp(currentTimestamp.toString());
                setDateTime(formatDateTime(currentTime));
              }}
              className="space-y-2 text-left p-3 rounded-lg hover:bg-white/60 dark:hover:bg-gray-700 transition-all group relative border border-orange-200 dark:border-gray-700 bg-white/40"
              title={language === 'zh' ? '单击复制，双击填充到输入框' : 'Click to copy, double-click to fill input'}
            >
              <p className="text-sm text-orange-700 dark:text-gray-400 flex items-center justify-between">
                {language === 'zh' ? 'Unix 时间戳（秒）' : 'Unix Timestamp (seconds)'}
                <Copy size={14} className="opacity-0 group-hover:opacity-100 transition-opacity text-orange-600 dark:text-orange-400" />
              </p>
              <p className="text-2xl font-mono font-bold text-orange-900 dark:text-gray-100">{currentTimestamp}</p>
              {copySuccess === 'current-ts' && (
                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 text-green-600 dark:text-green-400 px-3 py-1 rounded-lg text-sm font-medium shadow-lg flex items-center gap-1 border border-gray-200 dark:border-gray-700">
                  <CheckCircle size={16} /> {t('common.copied')}
                </span>
              )}
            </button>

            <button
              onClick={() => copyToClipboard(currentTimestampMs.toString(), 'current-tsms')}
              className="space-y-2 text-left p-3 rounded-lg hover:bg-white/60 dark:hover:bg-gray-700 transition-all group relative border border-orange-200 dark:border-gray-700 bg-white/40"
            >
              <p className="text-sm text-orange-700 dark:text-gray-400 flex items-center justify-between">
                {language === 'zh' ? 'Unix 时间戳（毫秒）' : 'Unix Timestamp (milliseconds)'}
                <Copy size={14} className="opacity-0 group-hover:opacity-100 transition-opacity text-orange-600 dark:text-orange-400" />
              </p>
              <p className="text-xl font-mono font-bold text-orange-900 dark:text-gray-100">{currentTimestampMs}</p>
              {copySuccess === 'current-tsms' && (
                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 text-green-600 dark:text-green-400 px-3 py-1 rounded-lg text-sm font-medium shadow-lg flex items-center gap-1 border border-gray-200 dark:border-gray-700">
                  <CheckCircle size={16} /> {t('common.copied')}
                </span>
              )}
            </button>

            <button
              onClick={() => copyToClipboard(currentISO, 'current-iso')}
              className="space-y-2 text-left p-3 rounded-lg hover:bg-white/60 dark:hover:bg-gray-700 transition-all group relative border border-orange-200 dark:border-gray-700 bg-white/40"
            >
              <p className="text-sm text-orange-700 dark:text-gray-400 flex items-center justify-between">
                {t('timestamp.output.iso8601')}
                <Copy size={14} className="opacity-0 group-hover:opacity-100 transition-opacity text-orange-600 dark:text-orange-400" />
              </p>
              <p className="text-lg font-mono font-bold break-all text-orange-900 dark:text-gray-100">{currentISO}</p>
              {copySuccess === 'current-iso' && (
                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 text-green-600 dark:text-green-400 px-3 py-1 rounded-lg text-sm font-medium shadow-lg flex items-center gap-1 border border-gray-200 dark:border-gray-700">
                  <CheckCircle size={16} /> {t('common.copied')}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* 转换器 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 时间戳 → 日期时间 */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Clock size={20} className="text-orange-600 dark:text-orange-400" />
              {language === 'zh' ? '时间戳 → 日期时间' : 'Timestamp → Date Time'}
            </h3>

            <div className="space-y-4">
              {/* 时区选择器 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                  <Globe size={16} />
                  {language === 'zh' ? '时区选择' : 'Timezone'}
                </label>
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400/50 dark:focus:ring-orange-400/50 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-shadow"
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {language === 'zh' ? 'Unix 时间戳（秒或毫秒）' : 'Unix Timestamp (seconds or milliseconds)'}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={timestamp}
                    onChange={handleTimestampChange}
                    placeholder={language === 'zh' ? '例如：1733645678 或 1733645678000' : 'e.g., 1733645678 or 1733645678000'}
                    className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400/50 dark:focus:ring-orange-400/50 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono transition-shadow"
                  />
                  <button
                    onClick={useCurrentTime}
                    className="px-4 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-all flex items-center gap-2 text-sm font-medium whitespace-nowrap shadow-sm hover:shadow"
                    title={language === 'zh' ? '使用当前时间' : 'Use current time'}
                  >
                    <RotateCw size={16} />
                    {language === 'zh' ? '现在' : 'Now'}
                  </button>
                </div>
              </div>

              {timestamp && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {language === 'zh' ? '转换结果' : 'Result'}
                  </label>
                  <div className="relative">
                    <div className="w-full px-4 py-3 bg-amber-50 dark:bg-gray-900/50 border border-amber-200 dark:border-gray-700 rounded-lg font-mono text-amber-900 dark:text-gray-200 font-semibold">
                      {dateTime}
                    </div>
                    <button
                      onClick={() => copyToClipboard(dateTime, 'date')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-gray-200 dark:hover:bg-gray-800 rounded transition-colors"
                      title={t('common.copy')}
                    >
                      {copySuccess === 'date' ? (
                        <CheckCircle size={18} className="text-amber-600 dark:text-amber-400" />
                      ) : (
                        <Copy size={18} className="text-gray-500 dark:text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 日期时间 → 时间戳 */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Calendar size={20} className="text-orange-600 dark:text-orange-400" />
              {language === 'zh' ? '日期时间 → 时间戳' : 'Date Time → Timestamp'}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {language === 'zh' ? '日期时间（多种格式）' : 'Date Time (multiple formats)'}
                </label>
                <input
                  type="text"
                  value={dateTime}
                  onChange={handleDateTimeChange}
                  placeholder={language === 'zh' ? '例如：2025-12-08 15:30:00' : 'e.g., 2025-12-08 15:30:00'}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400/50 dark:focus:ring-orange-400/50 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono transition-shadow"
                />
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  {language === 'zh'
                    ? '支持格式：2025-12-08、2025-12-08 15:30、2025/12/08、ISO 8601 等'
                    : 'Supported formats: 2025-12-08, 2025-12-08 15:30, 2025/12/08, ISO 8601, etc.'}
                </p>
              </div>

              {timestamp && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {language === 'zh' ? '转换结果' : 'Result'}
                  </label>
                  <div className="relative">
                    <div className="w-full px-4 py-3 bg-amber-50 dark:bg-gray-900/50 border border-amber-200 dark:border-gray-700 rounded-lg font-mono text-amber-900 dark:text-gray-200 font-semibold">
                      {timestamp}
                    </div>
                    <button
                      onClick={() => copyToClipboard(timestamp, 'timestamp')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-gray-200 dark:hover:bg-gray-800 rounded transition-colors"
                      title={t('common.copy')}
                    >
                      {copySuccess === 'timestamp' ? (
                        <CheckCircle size={18} className="text-amber-600 dark:text-amber-400" />
                      ) : (
                        <Copy size={18} className="text-gray-500 dark:text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 使用说明 */}
        <div className="space-y-4">
          {/* 基础使用说明 */}
          <div className="bg-orange-50 dark:bg-orange-900/20 border-l-4 border-orange-500 dark:border-orange-400 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-orange-900 dark:text-orange-300 mb-3">
              {language === 'zh' ? '💡 使用说明' : '💡 Instructions'}
            </h3>
            <div className="space-y-2 text-sm text-orange-800 dark:text-orange-300">
              {language === 'zh' ? (
                <>
                  <p>• <strong>Unix 时间戳</strong>：从 1970-01-01 00:00:00 UTC 开始的秒数或毫秒数</p>
                  <p>• <strong>10 位数字</strong>：秒级时间戳（例如：1733645678）</p>
                  <p>• <strong>13 位数字</strong>：毫秒级时间戳（例如：1733645678000）</p>
                  <p>• <strong>时区选择</strong>：可切换不同时区查看对应的本地时间</p>
                  <p>• <strong>快速复制</strong>：点击当前时间区域的任意值即可复制</p>
                  <p>• <strong>使用当前时间</strong>：点击按钮快速填充当前时间戳</p>
                </>
              ) : (
                <>
                  <p>• <strong>Unix Timestamp</strong>: Seconds or milliseconds since 1970-01-01 00:00:00 UTC</p>
                  <p>• <strong>10 digits</strong>: Seconds timestamp (e.g., 1733645678)</p>
                  <p>• <strong>13 digits</strong>: Milliseconds timestamp (e.g., 1733645678000)</p>
                  <p>• <strong>Timezone</strong>: Switch timezones to view corresponding local time</p>
                  <p>• <strong>Quick Copy</strong>: Click any value in the current time section to copy</p>
                  <p>• <strong>Use Current Time</strong>: Click button to fill current timestamp</p>
                </>
              )}
            </div>
          </div>

          {/* ISO 8601 格式说明 */}
          <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 dark:border-green-400 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-300 mb-3">
              {language === 'zh' ? '📅 ISO 8601 格式说明' : '📅 ISO 8601 Format'}
            </h3>
            <div className="space-y-3 text-sm text-green-800 dark:text-green-300">
              <p>
                {language === 'zh'
                  ? <><strong>ISO 8601</strong> 是国际标准的日期时间格式，由国际标准化组织（ISO）制定。</>
                  : <><strong>ISO 8601</strong> is the international standard for date and time format.</>}
              </p>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 font-mono text-xs">
                <div className="text-gray-600 dark:text-gray-400 mb-1">
                  {language === 'zh' ? '格式结构：' : 'Format structure:'}
                </div>
                <div className="text-green-700 dark:text-green-300">
                  <span className="bg-yellow-100 dark:bg-yellow-900/30">2025-12-09</span>
                  <span className="text-red-600 dark:text-red-400">T</span>
                  <span className="bg-blue-100 dark:bg-blue-900/30">03:32:25.963</span>
                  <span className="text-purple-600 dark:text-purple-400">Z</span>
                </div>
                <div className="mt-2 space-y-1 text-gray-600 dark:text-gray-400">
                  <div>• <span className="bg-yellow-100 dark:bg-yellow-900/30 px-1">2025-12-09</span> → {language === 'zh' ? '日期（年-月-日）' : 'Date (YYYY-MM-DD)'}</div>
                  <div>• <span className="text-red-600 dark:text-red-400">T</span> → {language === 'zh' ? '分隔符' : 'Separator'}</div>
                  <div>• <span className="bg-blue-100 dark:bg-blue-900/30 px-1">03:32:25.963</span> → {language === 'zh' ? '时间（时:分:秒.毫秒）' : 'Time (HH:mm:ss.SSS)'}</div>
                  <div>• <span className="text-purple-600 dark:text-purple-400">Z</span> → {language === 'zh' ? 'UTC 时区（也叫"祖鲁时间"）' : 'UTC timezone (Zulu time)'}</div>
                </div>
              </div>

              <div className="space-y-1">
                <p><strong>{language === 'zh' ? '为什么使用 ISO 8601？' : 'Why use ISO 8601?'}</strong></p>
                <p>✓ <strong>{language === 'zh' ? '全球统一' : 'Universal'}</strong>：{language === 'zh' ? '避免不同国家日期格式的歧义' : 'Avoids ambiguity between date formats'}</p>
                <p>✓ <strong>{language === 'zh' ? '程序友好' : 'Developer friendly'}</strong>：{language === 'zh' ? 'JavaScript、数据库都原生支持' : 'Native support in JavaScript and databases'}</p>
                <p>✓ <strong>{language === 'zh' ? '包含时区' : 'Timezone included'}</strong>：{language === 'zh' ? '精确表示时间，避免时区混淆' : 'Precise time representation'}</p>
                <p>✓ <strong>{language === 'zh' ? '可排序' : 'Sortable'}</strong>：{language === 'zh' ? '字符串排序 = 时间排序' : 'String sort = Time sort'}</p>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-xs">
                <div className="text-gray-600 dark:text-gray-400 mb-1">
                  {language === 'zh' ? '常见应用场景：' : 'Common use cases:'}
                </div>
                <div className="space-y-1 font-mono text-gray-700 dark:text-gray-300">
                  <div>• API {language === 'zh' ? '接口' : ''}：<code className="text-green-600 dark:text-green-400">"created_at": "2025-12-09T03:32:25Z"</code></div>
                  <div>• {language === 'zh' ? '数据库存储' : 'Database'}：<code className="text-green-600 dark:text-green-400">TIMESTAMP '2025-12-09T03:32:25Z'</code></div>
                  <div>• {language === 'zh' ? '日志记录' : 'Logging'}：<code className="text-green-600 dark:text-green-400">[2025-12-09T03:32:25.963Z] INFO ...</code></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TimestampConverter;
